// schema
import { parseISO } from "date-fns";

import { ICustomQuestionGetResponse, IEngagementSettings } from "@performyard/models";

import {
	ICohortByFactorResponse,
	ICohortENPSDataRequestParams,
	ICohortFactorSummaryPoint,
	ICohortRespondentCountsPoint,
	ICohortSatisfactionRangePoints,
	ICohortSatisfactionRangeResponse,
	ICohortSummaryPoint,
	ICohortSummaryResponse,
	ICohortSummaryResponses,
	IEngagementCurrentSurveySummary,
	IEngagementQuestionAnswersResponse,
	IEngagementQuestionRangeResponseSummary,
	IEngagementQuestionRangeResponseSummaryNormalized,
	IEngagementQuestionResponse,
	IFactorCohortSummaryRequestParams,
	IFactorComparisonPoint,
	IFactorComparisonRequestParams,
	IFactorSummaryPoint,
	IFactorSummaryResponse,
	IFactorsSummaryReqeustParams,
	INPSAdditionalFeedbackResponse,
	INPSRangeNormalized,
	INPSRangeResponse,
	INPSSummaryPoint,
	IPageSettings,
	IPageSettingsUpdate,
	IParticipationSummaryNormalized,
	IParticipationSummaryResponse,
	IQuestionsSummaryRequestParams,
	ISummaryPoint,
	ISummaryRequestParams,
	ISummaryResponse,
	SummaryResponse,
} from "../../../pages/Engagement/schema";

import { SearchServiceSort } from "../../hooks";

import request from "../base-request";
import { CustomQuestionData, CustomQuestionPatch } from "./engagement-survey-custom-questions";

type IEngagementSettingsWithoutDates = Omit<
	IEngagementSettings,
	"next_survey_date" | "last_survey_date" | "last_survey_reminder_date"
>;

type ReminderEmailSentEntry = {
	sent_at: Date;
	trigger: "manual" | "automatic";
};

type IEngagementSettingsNormalized = IEngagementSettingsWithoutDates & {
	next_survey_date?: Date | null;
	last_survey_date?: Date | null;
	last_survey_reminder_date?: Date | null;
	reminder_emails_sent?: Date | ReminderEmailSentEntry[] | null;
};

export const getCompanyEngagementSettings = async (): Promise<{
	engagement: IEngagementSettingsNormalized;
}> => {
	const [data] = await request<{
		engagement: IEngagementSettingsWithoutDates & {
			next_survey_date?: string;
			last_survey_date?: string;
			last_survey_reminder_date?: string;
			reminder_emails_sent?: string | Array<{ sent_at: string; trigger: "manual" | "automatic" }> | null;
		};
	}>("/api/engagement/settings/");

	const parseReminderEmailsSent = (
		value: string | Array<{ sent_at: string; trigger: "manual" | "automatic" }> | null | undefined
	): Date | ReminderEmailSentEntry[] | null => {
		if (!value) return null;
		if (typeof value === "string") {
			return parseISO(value);
		}
		if (Array.isArray(value)) {
			return value.map((entry) => ({
				sent_at: parseISO(entry.sent_at),
				trigger: entry.trigger,
			}));
		}
		return null;
	};

	return {
		...data,
		engagement: {
			...data.engagement,
			next_survey_date: data.engagement?.next_survey_date ? parseISO(data.engagement.next_survey_date) : null,
			last_survey_date: data.engagement?.last_survey_date ? parseISO(data.engagement.last_survey_date) : null,
			last_survey_reminder_date: data.engagement?.last_survey_reminder_date
				? parseISO(data.engagement.last_survey_reminder_date)
				: null,
			reminder_emails_sent: parseReminderEmailsSent(data.engagement?.reminder_emails_sent),
		},
	};
};

export const setCompanyEngagementSettings = async (
	body: Partial<
		Omit<IEngagementSettingsNormalized, "last_survey_date" | "last_survey_reminder_date" | "current_survey_status">
	>
): Promise<any> => {
	const normalizedBody: Partial<Omit<IEngagementSettingsWithoutDates, "current_survey_status">> & {
		next_survey_date?: string;
	} = {
		...body,
		next_survey_date: body.next_survey_date?.toISOString(),
	};
	const [data] = await request("/api/engagement/settings/", {
		method: "POST",
		body: JSON.stringify(normalizedBody),
	});

	return data;
};

export const setEngagementEnabled = async ({
	enabled,
	next_survey_date,
}: {
	enabled: boolean;
	next_survey_date: Date;
}): Promise<any> => {
	const [data] = await request("/api/engagement/settings/", {
		method: "POST",
		body: JSON.stringify({ enabled, next_survey_date }),
	});
	return data;
};
export const getOverallSummary = async (params: ISummaryRequestParams): Promise<SummaryResponse<ISummaryPoint[]>> => {
	const { startDate, endDate, binSize, cohortIds } = params;
	const queryString = new URLSearchParams();
	if (startDate) queryString.append("startDate", startDate.toISOString());
	if (endDate) queryString.append("endDate", endDate.toISOString());
	if (binSize) queryString.append("binSize", binSize);
	if (cohortIds) queryString.append("cohortIds", cohortIds);
	const [response] = await request<SummaryResponse<ISummaryResponse[]>>(
		`/api/engagement/overall_summary${queryString.toString().length ? `?${queryString.toString()}` : ""}`,
		{
			method: "GET",
		}
	);
	// type cast time_bucket in response
	const normalizedData = {
		...response,
		data: response.data.map((point) => ({
			...point,
			time_bucket: parseISO(point.time_bucket),
		})),
	};
	return normalizedData;
};

export const getCohortSummary = async (params: ISummaryRequestParams): Promise<ICohortSummaryResponses> => {
	const { startDate, endDate, binSize, cohortIds } = params;
	// create URL querystring with all optional params
	const queryString = new URLSearchParams();
	if (startDate) queryString.append("startDate", startDate.toISOString());
	if (endDate) queryString.append("endDate", endDate.toISOString());
	if (binSize) queryString.append("binSize", binSize);
	if (cohortIds) queryString.append("cohortIds", cohortIds);
	const [data] = await request<SummaryResponse<ICohortSummaryResponse[]>>(
		`/api/engagement/cohort_summary${queryString.toString().length ? `?${queryString.toString()}` : ""}`,
		{
			method: "GET",
		}
	);
	// type cast time_bucket
	return {
		cohortIds: cohortIds ?? "all",
		responses: data.data.map((point) => ({
			...point,
			time_bucket: parseISO(point.time_bucket),
		})) as ICohortSummaryPoint[],
	};
};

export const getFactorsSummary = async (
	params: IFactorsSummaryReqeustParams
): Promise<SummaryResponse<IFactorSummaryPoint[]>> => {
	const { startDate, endDate, month, binSize, cohortIds, overall } = params;
	const queryString = new URLSearchParams();
	if (startDate) queryString.append("startDate", startDate.toISOString());
	if (endDate) queryString.append("endDate", endDate.toISOString());
	if (month) queryString.append("month", month.toISOString());
	if (binSize) queryString.append("binSize", binSize);
	if (cohortIds) queryString.append("cohortIds", cohortIds);
	if (overall) queryString.append("overall", overall);
	const [data] = await request<SummaryResponse<IFactorSummaryResponse[]>>(
		`/api/engagement/factor_summary${queryString.toString().length ? `?${queryString.toString()}` : ""}`,
		{
			method: "GET",
		}
	);
	// type cast time_bucket
	const normalizedData = {
		...data,
		data: data.data.map((point) => ({
			...point,
			time_bucket: point.time_bucket ? parseISO(point.time_bucket) : undefined,
		})),
	};
	return normalizedData;
};

export const getNPSSummary = async (
	params: ICohortENPSDataRequestParams
): Promise<SummaryResponse<INPSSummaryPoint[]>> => {
	const { startDate, endDate, cohortIds } = params;
	const byCohort = (params as ICohortENPSDataRequestParams).byCohort;

	const queryString = new URLSearchParams();
	if (startDate) queryString.append("startDate", startDate.toISOString());
	if (endDate) queryString.append("endDate", endDate.toISOString());
	if (cohortIds) queryString.append("cohortIds", cohortIds);
	if (byCohort) queryString.append("byCohort", byCohort);
	const [data] = await request<SummaryResponse<INPSSummaryPoint[]>>(
		`/api/engagement/nps_summary${queryString.toString().length ? `?${queryString.toString()}` : ""}`,
		{
			method: "GET",
		}
	);
	// no date in response, so no need to type cast
	return data;
};

export const getCohortSatisfactionRange = async (
	params: ISummaryRequestParams
): Promise<ICohortSatisfactionRangePoints> => {
	const { startDate, endDate } = params;
	const queryString = new URLSearchParams();
	if (startDate) queryString.append("startDate", startDate.toISOString());
	if (endDate) queryString.append("endDate", endDate.toISOString());
	const [
		{
			data: { min, max },
		},
	] = await request<SummaryResponse<ICohortSatisfactionRangeResponse>>(
		`/api/engagement/cohort-satisfaction-range${queryString.toString().length ? `?${queryString.toString()}` : ""}`,
		{
			method: "GET",
		}
	);
	// type cast response to IICohortSatisfactionRangePoints
	return {
		min: {
			...min,
			monthlyAverages: min.monthlyAverages.map((point) => ({
				...point,
				time_bucket: parseISO(point.time_bucket),
			})),
		},
		max: {
			...max,
			monthlyAverages: max.monthlyAverages.map((point) => ({
				...point,
				time_bucket: parseISO(point.time_bucket),
			})),
		},
	};
};

export const getParticipationSummary = async (
	params: ISummaryRequestParams
): Promise<SummaryResponse<IParticipationSummaryNormalized>> => {
	const { startDate, endDate } = params;
	const queryString = new URLSearchParams();
	if (startDate) queryString.append("startDate", startDate.toISOString());
	if (endDate) queryString.append("endDate", endDate.toISOString());
	const [data] = await request<SummaryResponse<IParticipationSummaryResponse>>(
		`/api/engagement/participation_rate${queryString.toString().length ? `?${queryString.toString()}` : ""}`,
		{
			method: "GET",
		}
	);
	const normalizedData = {
		...data,
		data: {
			...data?.data,
			monthlyAverages:
				data?.data?.monthlyAverages?.map((point) => ({
					...point,
					time_bucket: parseISO(point.time_bucket as string),
				})) || [],
		},
	};
	return normalizedData;
};

export const getFactorComparison = async (
	params: IFactorComparisonRequestParams
): Promise<IFactorComparisonPoint[]> => {
	const { startDate, endDate, xAxis, yAxis } = params;
	const queryString = new URLSearchParams();
	if (startDate) queryString.append("startDate", startDate.toISOString());
	if (endDate) queryString.append("endDate", endDate.toISOString());
	if (xAxis) queryString.append("xAxis", xAxis);
	if (yAxis) queryString.append("yAxis", yAxis);
	const [{ data }] = await request<SummaryResponse<IFactorComparisonPoint[]>>(
		`/api/engagement/factor_comparison${queryString.toString().length ? `?${queryString.toString()}` : ""}`,
		{
			method: "GET",
		}
	);

	return data;
};

export const getPageSettings = async (): Promise<IPageSettings> => {
	const [{ data }] = await request<SummaryResponse<IPageSettings>>("/api/engagement/dashboard_page_settings/", {
		method: "GET",
	});
	return data;
};

export const updatePageSettings = async (settings: IPageSettingsUpdate): Promise<IPageSettings> => {
	const [{ data }] = await request<SummaryResponse<IPageSettings>>("/api/engagement/dashboard_page_settings/", {
		method: "PATCH",
		body: JSON.stringify(settings),
	});
	return data;
};

export const getCohortFactorSummary = async (
	params: IFactorCohortSummaryRequestParams
): Promise<{ factorsData: ICohortFactorSummaryPoint[]; respondentCounts: ICohortRespondentCountsPoint[] }> => {
	const { startDate, endDate, include_overall } = params;
	const queryString = new URLSearchParams();
	if (startDate) queryString.append("startDate", startDate.toISOString());
	if (endDate) queryString.append("endDate", endDate.toISOString());
	if (include_overall) queryString.append("include_overall", include_overall);
	const [{ data }] = await request<SummaryResponse<ICohortByFactorResponse>>(
		`/api/engagement/cohort_factor_summary${queryString.toString().length ? `?${queryString.toString()}` : ""}`,
		{
			method: "GET",
		}
	);
	return {
		factorsData: data?.factor_by_cohort || [],
		respondentCounts: data?.respondent_counts || [],
	};
};

export const getNPSSummaryRange = async (
	params: ISummaryRequestParams
): Promise<SummaryResponse<INPSRangeNormalized[]>> => {
	const { startDate, endDate, cohortIds } = params;
	const queryString = new URLSearchParams();
	if (startDate) queryString.append("startDate", startDate.toISOString());
	if (endDate) queryString.append("endDate", endDate.toISOString());
	if (cohortIds) queryString.append("cohortIds", cohortIds);
	const [data] = await request<SummaryResponse<INPSRangeResponse[]>>(
		`/api/engagement/nps-summary-range${queryString.toString().length ? `?${queryString.toString()}` : ""}`,
		{
			method: "GET",
		}
	);
	const normalizedData = {
		...data,
		cohortIds,
		data: data?.data.map((point) => ({
			...point,
			time_bucket: parseISO(point.time_bucket),
		})),
	};
	return normalizedData;
};

export const getCurrentSurveySummary = async (): Promise<SummaryResponse<IEngagementCurrentSurveySummary[]>> => {
	const [data] = await request<SummaryResponse<IEngagementCurrentSurveySummary[]>>(
		`/api/engagement/current_survey_summary`,
		{
			method: "GET",
		}
	);
	return data;
};

export const getQuestionsSummary = async (
	params: IQuestionsSummaryRequestParams
): Promise<SummaryResponse<IEngagementQuestionResponse[]>> => {
	const { startDate, endDate, cohortIds, factor, search } = params;
	// create URL querystring with all optional params
	const queryString = new URLSearchParams();
	if (startDate) queryString.append("startDate", startDate.toISOString());
	if (endDate) queryString.append("endDate", endDate.toISOString());
	if (cohortIds) queryString.append("cohortIds", cohortIds);
	if (factor) queryString.append("factor", factor);
	if (search) queryString.append("search", search);
	const [data] = await request<SummaryResponse<IEngagementQuestionResponse[]>>(
		`/api/engagement/questions_summary${queryString.toString().length ? `?${queryString.toString()}` : ""}`,
		{
			method: "GET",
		}
	);
	return data;
};

export const getNPSAdditionalFeedback = async (params) => {
	const { startDate, endDate, cohortIds } = params;
	const queryString = new URLSearchParams();
	if (startDate) queryString.append("startDate", startDate.toISOString());
	if (endDate) queryString.append("endDate", endDate.toISOString());
	if (cohortIds) queryString.append("cohortIds", cohortIds);

	const url = `/api/engagement/nps_additional_feedback${queryString.toString() ? `?${queryString.toString()}` : ""}`;

	const [data] = await request<SummaryResponse<INPSAdditionalFeedbackResponse[]>>(url, {
		method: "GET",
	});
	return data;
};

export const getQuestionsSummaryRange = async (
	params: ISummaryRequestParams
): Promise<SummaryResponse<IEngagementQuestionRangeResponseSummaryNormalized>> => {
	const { startDate, endDate } = params;
	// create URL querystring with all optional params
	const queryString = new URLSearchParams();
	if (startDate) queryString.append("startDate", startDate.toISOString());
	if (endDate) queryString.append("endDate", endDate.toISOString());
	const [data] = await request<SummaryResponse<IEngagementQuestionRangeResponseSummary>>(
		`/api/engagement/questions_summary_range${queryString.toString().length ? `?${queryString.toString()}` : ""}`,
		{
			method: "GET",
		}
	);
	const normalizedData = {
		...data,
		data: {
			...data.data,
			monthly_averages:
				data?.data?.monthly_averages?.map((point) => ({
					...point,
					// parseISO and then parse from UTC to local time
					time_bucket: parseISO(point.time_bucket),
				})) || [],
		},
	};
	return normalizedData;
};

export type ClientCustomQuestionGetResponse = Omit<
	ICustomQuestionGetResponse,
	"_id" | "company_id" | "created_by" | "updated_by" | "updated_by_employee"
> & {
	_id: string;
	company_id: string;
	created_by: string;
	updated_by: string;
	updated_by_employee: {
		_id: string;
		name: string;
	};
};

export type ClientCustomQuestionData = Omit<CustomQuestionData, "data"> & {
	data: Omit<CustomQuestionData["data"], "results"> & {
		results: ClientCustomQuestionGetResponse[];
	};
};

export const getEngagementSurveyQuestions = async (
	sort?: SearchServiceSort,
	search = ""
): Promise<ClientCustomQuestionData["data"]> => {
	let urlAppend = sort ? `?sortOn=${sort.name}&sortDirection=${sort.direction}` : "";
	if (search) {
		urlAppend += sort ? `&search=${search}` : `?search=${search}`;
	}
	try {
		const [surveyQuestions] = await request<ClientCustomQuestionData>(`/api/engagement/questions/${urlAppend}`);
		if (surveyQuestions.errors.length > 0) {
			throw new Error(surveyQuestions.errors[0]);
		}
		return surveyQuestions.data;
	} catch (error) {
		throw error;
	}
};

export const updateQuestionStatus = async (question: Partial<ICustomQuestionGetResponse>) => {
	const editableFields = ["enabled"];
	// get only the fields that are editable
	const questionPatch: CustomQuestionPatch = Object.keys(question).reduce((acc, key) => {
		if (editableFields.includes(key)) {
			acc[key] = question[key];
		}
		return acc;
	}, {} as CustomQuestionPatch);

	const [updatedQuestion] = await request(`/api/engagement/questions/${question.id}`, {
		method: "POST",
		body: JSON.stringify(questionPatch),
	});
	return updatedQuestion;
};

export const getEngagementSurveyQuestionAnswers = async (
	questionId: string,
	startDate?: Date,
	endDate?: Date,
	cohortIds?: string
): Promise<SummaryResponse<IEngagementQuestionAnswersResponse[]>> => {
	const queryString = new URLSearchParams();
	if (startDate) queryString.append("startDate", startDate.toISOString());
	if (endDate) queryString.append("endDate", endDate.toISOString());
	if (cohortIds) queryString.append("cohortIds", cohortIds);
	const finalQueryString = queryString.toString().length ? `?${queryString.toString()}` : "";
	const [questionAnswers] = await request<SummaryResponse<IEngagementQuestionAnswersResponse[]>>(
		`/api/engagement/questions/${questionId}/answers${finalQueryString}`,
		{
			method: "GET",
		}
	);
	return questionAnswers;
};
